import React from 'react'
import PropTypes from 'prop-types'
import { Global, css } from '@emotion/core'
import { Paragraph } from 'components/patterns'
import strings from 'utils/strings'
import { useSelector } from 'hooks/redux'
import { getVehicleSubType } from 'selectors/truck'
import DividerPipe from 'components/DividerPipe'
import { prop } from 'ramda'
import {
  StyledModalContent,
  StyledList,
  StyledOl,
  StyledModelHeaderContainer,
  WarningIcon,
  HeaderTitle,
  StyledModal,
  StyledHeaderContainer,
  StyledIoSwitchGroup,
  CloseButton,
  CloseIcon,
} from './FeatureInputModal.style'

// PBI 147363 - Documentation 3: Global styles for the switch selection modal
const modalStyles = css`
  .chec-error-modal .ant-modal-close {
    display: none !important;
  }

  .chec-error-modal .ant-modal-header {
    padding: 16px 25px !important;
    border-bottom: 1px solid #e3e5e8;
  }

  .chec-error-modal .ant-modal-body {
    padding: 25px 25px 45px 25px !important;
  }

  /* Make the title container span full width so close button can be on the right */
  .chec-error-modal .ant-modal-title {
    width: 100%;
  }

  .chec-error-modal .ant-modal-footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    border-top: 1px solid #e3e5e8;
    background: #fff;
    padding: 16px;
    gap: 16px;
  }
`

const stripSignalType = (name) =>
  name ? name.replace(/\s*\([^)]*\)\s*$/, '') : ''

const FeatureInputModal = (props) => {
  const {
    isOpen,
    onClickSelect,
    onClickCancel,
    featureInput,
    serialNumber,
    selectedSearchFeatures,
  } = props

  const subType = useSelector(getVehicleSubType, { serialNumber })

  const lists = selectedSearchFeatures.map((feature) => {
    const ioSwitches = featureInput.filter(
      ({ featureId }) => feature.id === featureId
    )

    if (ioSwitches.length) {
      const list = ioSwitches.map(({ description }) => (
        <li key={description}>
          <Paragraph>
            {stripSignalType(description)} {`(${subType}`} <DividerPipe />
            {`${prop('moduleName', feature)})`}
          </Paragraph>
        </li>
      ))

      const featureDisplay = strings.feature_display_with_revision({
        partNumber: feature.partNumber,
        revision: feature.revision,
      })
      const title = `${featureDisplay} has ${ioSwitches.length} unconnected switch signals.`

      return (
        <StyledIoSwitchGroup key={feature.id}>
          <Paragraph bold style={{ marginBottom: 4 }}>
            {title}
          </Paragraph>
          <StyledOl>{list}</StyledOl>
        </StyledIoSwitchGroup>
      )
    }

    return null
  })

  return (
    <>
      <Global styles={modalStyles} />
      <StyledModal
        closeButton={false}
        noHandler={onClickCancel}
        header={
          <StyledModelHeaderContainer>
            <WarningIcon />
            <HeaderTitle>{strings.switch_selections_needed}</HeaderTitle>
            <CloseButton onClick={onClickCancel} aria-label="Close modal">
              <CloseIcon />
            </CloseButton>
          </StyledModelHeaderContainer>
        }
        isOpen={isOpen}
        className="chec-error-modal"
        customButtons={[
          {
            text: strings.cancel,
            onClick: onClickCancel,
            type: 'secondary',
          },
          {
            text: strings.select_switchs,
            onClick: onClickSelect,
            type: 'primary',
          },
        ]}
      >
        <StyledModalContent>
          <StyledList key={strings.unconnected_switch_signals}>
            <StyledHeaderContainer>
              <span>{strings.at_least_one_feature_needs_switch}</span>
            </StyledHeaderContainer>
            {lists}
          </StyledList>
        </StyledModalContent>
      </StyledModal>
    </>
  )
}

FeatureInputModal.propTypes = {
  isOpen: PropTypes.bool,
  serialNumber: PropTypes.string,
  onClickCancel: PropTypes.func,
  onClickSelect: PropTypes.func,
  featureInput: PropTypes.array,
  selectedSearchFeatures: PropTypes.array,
}

export default FeatureInputModal
